import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core'
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import {
  AirbnbChannelService,
  AirbnbReservationAlterationPriceQuoteResponse,
  AirbnbReservationAlterationResponse,
  isPendingAirbnbAlteration,
} from '@tokeet-frontend/channels'
import { ActionFailed, Rental, selectRentalByAirbnbListingId, selectRentalById } from '@tokeet-frontend/tv3-platform'
import { Toaster } from '@tokeet-frontend/tv3-platform'
import { LoadInquiryDetails, BookingAlterationChanged } from '@tv3/store/inquiry/inquiry.actions'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { EMPTY, Observable } from 'rxjs'
import { catchError, delay, finalize } from 'rxjs/operators'

@Component({
  selector: 'app-airbnb-reservation-alteration-dialog',
  templateUrl: './airbnb-reservation-alteration-dialog.component.html',
  styleUrls: ['./airbnb-reservation-alteration-dialog.component.scss'],
  host: { class: 'modal-content' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AirbnbReservationAlterationDialogComponent implements OnInit {
  get alterationId() {
    return this.alteration?.['id']
  }

  get inquiry() {
    return this.data.inquiry
  }

  alteration: AirbnbReservationAlterationPriceQuoteResponse | AirbnbReservationAlterationResponse
  isPending = false

  get isHostAlteration() {
    return this.alteration?.initiated_by === 'host'
  }
  get isGuestAlteration() {
    return this.alteration?.initiated_by === 'guest'
  }

  sourceRental$: Observable<Rental>
  targetRental$: Observable<Rental>

  isLoading = false
  constructor(
    private store: Store<any>,
    public dialogRef: MatDialogRef<AirbnbReservationAlterationDialogComponent>,
    private toaster: Toaster,
    private airbnbChannelService: AirbnbChannelService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      inquiry: Inquiry
      alteration: AirbnbReservationAlterationPriceQuoteResponse | AirbnbReservationAlterationResponse
      customTotalPrice?: number
    }
  ) {
    this.alteration = this.data.alteration
    this.isPending = isPendingAirbnbAlteration(this.alteration as AirbnbReservationAlterationResponse)
  }

  ngOnInit(): void {
    const listingId =
      !this.alteration?.listing_id || this.alteration.listing_id === 'null' ? '' : this.alteration.listing_id
    this.targetRental$ = listingId
      ? this.store.pipe(select(selectRentalByAirbnbListingId(listingId)))
      : this.store.pipe(select(selectRentalById(this.inquiry.rentalId)))

    this.sourceRental$ = this.store.pipe(select(selectRentalById(this.inquiry.rentalId)))
  }

  close() {
    this.dialogRef.close()
  }

  doAction(action: 'DECLINED' | 'ACCEPTED' | 'CANCELED') {
    this.isLoading = true
    return this.airbnbChannelService.updateReservationAlterationById(this.alterationId, action).pipe(
      delay(5000),
      finalize(() => (this.isLoading = false)),
      catchError((error) => {
        this.store.dispatch(ActionFailed({ error }))
        return EMPTY
      })
    )
  }

  onInitialAlteration() {
    const quote = this.alteration
    this.isLoading = true
    this.airbnbChannelService
      .initAlteration(this.inquiry.inquiryId, {
        start_date: quote.start_date,
        end_date: quote.end_date,
        number_of_adults: quote.guest_details.number_of_adults,
        number_of_children: quote.guest_details.number_of_children,
        number_of_infants: quote.guest_details.number_of_infants,
        number_of_pets: quote.guest_details.number_of_pets,
        listing_id: quote.listing_id,
        total_price_override: this.data.customTotalPrice,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ({ id }) => {
          this.toaster.success($localize`Reservation alteration initiated Successfully`)
          this.store.dispatch(LoadInquiryDetails({ id: this.inquiry.id }))
          this.store.dispatch(
            BookingAlterationChanged({ inquiryId: this.inquiry.id, alterationId: id, source: 'airbnb' })
          )
          this.dialogRef.close('initiated')
        },
        (error) => {
          this.toaster.error($localize`Unable to initiate reservation alteration`, '', error)
        }
      )
  }

  onDecline() {
    this.doAction('DECLINED').subscribe(() => {
      this.store.dispatch(LoadInquiryDetails({ id: this.inquiry.id }))
      this.store.dispatch(
        BookingAlterationChanged({ inquiryId: this.inquiry.id, alterationId: this.alterationId, source: 'airbnb' })
      )
      this.close()
    })
  }

  onAccept() {
    this.doAction('ACCEPTED').subscribe(() => {
      this.store.dispatch(LoadInquiryDetails({ id: this.inquiry.id }))
      this.store.dispatch(
        BookingAlterationChanged({ inquiryId: this.inquiry.id, alterationId: this.alterationId, source: 'airbnb' })
      )
      this.close()
    })
  }

  onCancel() {
    this.doAction('CANCELED').subscribe(() => {
      this.store.dispatch(LoadInquiryDetails({ id: this.inquiry.id }))
      this.close()
    })
  }
}
